import React from 'react';
import { useSettings } from 'services/settings/SettingsContext';
import { ListColumnType, ListFilterType, QueryParamType } from 'types/ListTypes';
import AbstractList from 'components/lists/AbstractList';
import { proposalListFilters } from './ProposalList';
import { renderDate, renderDocumentNumber, renderPriceDecimalColumn, renderStatusBadge } from 'components/lists/functions/renderListColumn';
import ProposalModal from './ProposalModal';
import { LocationType, PersonType } from '../../views/contacts/ContactTypes';

const proposalDetailListConfigurations: Record<string, { params: (itemId: string) => QueryParamType }> = {
    contact: { params: (itemId: string) => ({ 'recipient': itemId }) },
    organization: { params: (itemId: string) => ({ 'recipient': itemId }) },
    person: { params: (itemId: string) => ({ 'recipient': itemId }) },
    location: { params: (itemId: string) => ({ 'location': itemId }) },
    deal: { params: (itemId: string) => ({ 'deal': itemId }) }
};

const ProposalDetailList: React.FC<{ itemId: string, detailPage: string, data: any }> = ({ itemId, detailPage, data }) => {
    const { userLocale } = useSettings();

    // Get the detail list configuration
    const configuration = proposalDetailListConfigurations[detailPage];
    const defaultParams = configuration.params(itemId);

    // Specify the columns of this list
    const columns: ListColumnType[] = [
        { 
            width: '1fr', 
            field: 'number', 
            label: 'proposal.general.proposal_number_label', 
            render: (_, row) => renderDocumentNumber(row.number, row.status, 'proposal') 
        },
        { 
            width: '2fr', 
            field: 'internal_reference', 
            label: 'proposal.general.internal_reference_label' 
        },
        { 
            width: '1fr', 
            field: 'proposal_date', 
            label: 'proposal.general.date_label', 
            render: (_, row) => renderDate(row.proposal_date, userLocale) 
        },
        { 
            width: '1fr', 
            field: 'amount', 
            label: 'proposal.general.amount_label', 
            render: (_, row) => renderPriceDecimalColumn(row.amount, row.currency, userLocale) 
        },
        { 
            width: '144px', 
            field: 'status', 
            label: 'proposal.general.status_label', 
            disableOrdering: true,
            render: (_, row) => renderStatusBadge(row.status) 
        }
    ];

    // Add the page params to the default list filters
    const applyPageParamsToFilters = (filters: ListFilterType[], pageParams: QueryParamType) => {
        return filters.map(filter => {
            if ('filters' in filter) {
                filter.filters = filter.filters.map(subFilter => ({
                    ...subFilter,
                    params: { ...subFilter.params, ...pageParams }
                }));
            } else {
                filter.params = { ...(filter.params || {}), ...pageParams };
            }
            return filter;
        });
    };

    // Specify detail modal for this list
    const detailModal = <ProposalModal />;
    const onRowClick = 'detailModal'
    
    // Configure post new item object
    const postNewItem = (detailPage: string) => {
        let postDataConfig: { url: string; linkedItem?: Record<string, string | number>} = { url: 'post_proposal' };
        let linkedItem: Record<string, string | number> | undefined;

        switch (detailPage) {
            case 'contact':
                // Get the primary location of the contact
                const contactPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the primary persons of the contact
                const contactPrimaryPersons = data.primary_persons?.filter((person: PersonType) => !person.deleted) || [];

                linkedItem = { 
                    'recipient': itemId,
                    'recipient_person': contactPrimaryPersons.length === 1 ? contactPrimaryPersons[0].id : null,
                    'address': contactPrimaryLocations.length === 1 ? contactPrimaryLocations[0].id : null,
                };
                break;
            case 'organization':
                // Get the primary location of the organization
                const organizationPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                // Get the primary persons of the organization
                const organizationPrimaryPersons = data.primary_persons?.filter((person: PersonType) => !person.deleted) || [];

                linkedItem = { 
                    'recipient': itemId,
                    'recipient_person': organizationPrimaryPersons.length === 1 ? organizationPrimaryPersons[0].id : null,
                    'address': organizationPrimaryLocations.length === 1 ? organizationPrimaryLocations[0].id : null,
                };
                break;
            case 'person':
                // Get the primary location of the person
                const personPrimaryLocations = data.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                linkedItem = { 
                    'recipient': itemId,
                    'address': personPrimaryLocations.length === 1 ? personPrimaryLocations[0].id : null,
                };
                break;
            case 'location':
                linkedItem = { 
                    'location': itemId 
                };
                break;
            case 'deal':
                // Get the primary location of the contact
                const dealContactPrimaryLocations = data.contact?.primary_locations?.filter((location: LocationType) => !location.deleted) || [];

                linkedItem = { 
                    'recipient': data.contact?.id, 
                    'recipient_person': data.contact_person?.id, 
                    'address': dealContactPrimaryLocations.length === 1 ? dealContactPrimaryLocations[0].id : null,
                    'deal': itemId 
                };
                break;
            default:
                break;
        }

        // Add the linked item to the post data config
        if (linkedItem) {
            postDataConfig.linkedItem = linkedItem;
        }

        return postDataConfig;
    };

    return (
        <div className='detail-list'>
            <AbstractList
                query={{
                    endpoint: 'get_proposal_list',
                    defaultParams,
                    object: 'proposal'
                }}
                objectName='proposal.general.object_name'
                columns={columns}
                filters={applyPageParamsToFilters(proposalListFilters, defaultParams)}
                showSearch={true}
                buttonSize='small'
                isPaginated={true}
                postNewItem={postNewItem(detailPage)}
                detailModal={detailModal}
                onRowClick={onRowClick}
            />
        </div>
    );
};

export default ProposalDetailList;